import type { SymbolKey } from '@/entities/symbol';
import type { SetState } from '@/types/helpers';

import type { ExportOption } from './exportConfig.types';
import type { ExportSymbolEntry } from '../utils/transformSymbols';

export enum ExportTypes {
  DATA = 'data',
  IMAGE = 'image'
}

export enum DateRangeType {
  ALL = 'all',
  RANGE = 'range',
  DATES = 'dates'
}

export interface ExportSettingsType {
  from: Date | null;
  to: Date | null;
  periodNumber: string;
  period: string;
  exportType: ExportTypes;
  exportSubType?: string;
  dateRangeType: DateRangeType;
  includeMetadata: boolean;
}

export const defaultExportSettings: ExportSettingsType = {
  from: null,
  to: null,
  periodNumber: '2',
  period: 'm',
  exportType: ExportTypes.DATA,
  dateRangeType: DateRangeType.ALL,
  includeMetadata: false
};

export type ExportRequestOverrides = {
  type: string;
  id?: string;
  exportSubType?: string | null;
  from?: string | null;
  to?: string | null;
  includeMetadata?: boolean;
  timezone?: string;
  dateFormat?: string;
  symbols?: SymbolKey[];
  group?: string;
  name?: string | null;
};

export interface ExportRequestBody {
  id?: string;
  name: string | null;
  exportType: string;
  exportSubType: string | null;
  from?: string | null;
  to?: string | null;
  timezone?: string;
  dateFormat?: string;
  includeMetadata?: boolean;
  symbols?: ExportSymbolEntry[];
}

export type ExcelExportStatus = 'idle' | 'loading' | 'queued';

export type ExportContextType = {
  previewOptionId: string | null;
  setPreviewOptionId: (id: string | null) => void;
  showSuccessScreen: boolean;
  setShowSuccessScreen: (show: boolean) => void;
  exportName: string | null;
  setExportName: SetState<string | null>;
  exportData: (type: string) => Promise<unknown>;
  exportDataWithParams: (params: ExportRequestOverrides) => Promise<unknown>;
  loading: boolean;
  exportError: boolean;
  isExcelPreview: boolean;
  capturePosthogEvent: () => void;
  exportOption: ExportOption | null;
  handleCopyToClipBoard: (textToCopy: string) => void;
  handleExportData: () => Promise<unknown>;
  scrambleCurlApiKey: (content: string) => string;
  scrambleApiToken: (content: string) => string;
  exportApiRequestString: string;
  uniqueSymbols: Set<SymbolKey>;
  exportCurlRequestString: string;
  exportSettings: ExportSettingsType;
  setExportSettings: SetState<ExportSettingsType>;
  timezone: string;
  resetExportState: () => void;
  // Excel export modal state
  excelExportStatus: ExcelExportStatus;
  setExcelExportStatus: (status: ExcelExportStatus) => void;
  resetExcelExportStatus: () => void;

  // Preview loading state
  previewLoading: boolean;
  setPreviewLoading: (loading: boolean) => void;

  // Preview download function for client-side downloads
  previewDownloadFn: (() => void) | null;
  setPreviewDownloadFn: (fn: (() => void) | null) => void;
};
